import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah, KATEGORI } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { PackageOpen, ArrowRight, Trash2, Boxes } from "lucide-react";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

const TIPE_CLS = {
  konversi: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  rusak: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  lainnya: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

export default function StockAdjustment() {
  const [inventory, setInventory] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [tipe, setTipe] = useState("konversi");
  const [sourceId, setSourceId] = useState("");
  const [sourceQty, setSourceQty] = useState(1);
  const [targetId, setTargetId] = useState("");
  const [targetQty, setTargetQty] = useState(1);
  const [catatan, setCatatan] = useState("");
  const [saving, setSaving] = useState(false);

  const loadInventory = useCallback(async () => {
    const { data } = await api.get("/inventory");
    setInventory(data);
  }, []);
  const loadAdjustments = useCallback(async () => {
    const { data } = await api.get("/adjustments");
    setAdjustments(data);
  }, []);

  useEffect(() => {
    loadInventory();
    loadAdjustments();
  }, [loadInventory, loadAdjustments]);

  const submit = async () => {
    if (!sourceId) return toast.error("Pilih produk sumber");
    if (tipe === "konversi" && !targetId) return toast.error("Pilih produk hasil konversi");
    setSaving(true);
    try {
      await api.post("/adjustments", {
        tipe,
        source_id: sourceId,
        source_qty: Number(sourceQty),
        target_id: tipe === "konversi" ? targetId : null,
        target_qty: tipe === "konversi" ? Number(targetQty) : 0,
        catatan,
      });
      toast.success("Penyesuaian stok tercatat");
      setSourceId(""); setSourceQty(1); setTargetId(""); setTargetQty(1); setCatatan("");
      loadInventory();
      loadAdjustments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Batalkan penyesuaian ini? Stok akan dikembalikan ke kondisi semula.")) return;
    await api.delete(`/adjustments/${id}`);
    toast.success("Penyesuaian dibatalkan");
    loadInventory();
    loadAdjustments();
  };

  const opt = (it) => `${it.nama_produk} — stok ${it.stok_qty}`;

  return (
    <div>
      <PageHeader title="Penyesuaian Stok" subtitle="Konversi Case / Box / Pack & Barang Rusak" />

      {/* Form */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <div className="flex gap-1.5 mb-5">
          {[
            { k: "konversi", label: "Konversi (Buka Case/Box)" },
            { k: "rusak", label: "Barang Rusak" },
            { k: "lainnya", label: "Lainnya" },
          ].map((t) => (
            <button key={t.k} data-testid={`adj-tipe-${t.k}`} onClick={() => setTipe(t.k)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${tipe === t.k ? "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40" : "bg-slate-800/50 text-slate-400 border border-transparent hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
          <div className="lg:col-span-4">
            <label className="block text-xs text-slate-400 mb-1.5">{tipe === "konversi" ? "Produk Sumber (dibuka)" : "Produk"}</label>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={inputCls} data-testid="adj-source">
              <option value="">Pilih produk...</option>
              {inventory.map((it) => <option key={it.id} value={it.id}>{opt(it)}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs text-slate-400 mb-1.5">Jumlah</label>
            <input type="number" min="1" value={sourceQty} onChange={(e) => setSourceQty(e.target.value)} className={inputCls} data-testid="adj-source-qty" />
          </div>

          {tipe === "konversi" && (
            <>
              <div className="lg:col-span-1 flex justify-center pb-2 text-indigo-400"><ArrowRight className="w-5 h-5" /></div>
              <div className="lg:col-span-3">
                <label className="block text-xs text-slate-400 mb-1.5">Produk Hasil</label>
                <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className={inputCls} data-testid="adj-target">
                  <option value="">Pilih produk...</option>
                  {inventory.filter((it) => it.id !== sourceId).map((it) => <option key={it.id} value={it.id}>{opt(it)}</option>)}
                </select>
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs text-slate-400 mb-1.5">Jumlah Hasil</label>
                <input type="number" min="1" value={targetQty} onChange={(e) => setTargetQty(e.target.value)} className={inputCls} data-testid="adj-target-qty" />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 mt-4">
          <input value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Catatan (mis. buka 1 case jadi 6 box)" className={`${inputCls} sm:col-span-9`} data-testid="adj-catatan" />
          <button onClick={submit} disabled={saving} className="sm:col-span-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition disabled:opacity-60 shadow-lg shadow-indigo-600/30" data-testid="submit-adjustment-button">
            <PackageOpen className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan & Buat Invoice"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">Konversi mengurangi stok sumber & menambah stok hasil. Barang rusak hanya mengurangi stok. Setiap penyesuaian punya kode invoice untuk pelacakan.</p>
      </div>

      {/* History */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800"><h3 className="font-heading font-semibold text-white">Riwayat Penyesuaian Stok</h3></div>
        {adjustments.length === 0 ? (
          <EmptyState icon={Boxes} title="Belum ada penyesuaian" subtitle="Catat konversi case→box→pack atau barang rusak untuk melacak perubahan stok." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="adjustments-table">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Kode Invoice</th>
                  <th className="text-left px-5 py-3">Tanggal</th>
                  <th className="text-left px-5 py-3">Tipe</th>
                  <th className="text-left px-5 py-3">Perubahan</th>
                  <th className="text-left px-5 py-3">Catatan</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3 font-mono text-xs text-indigo-300">{a.kode}</td>
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-5 py-3"><Badge className={TIPE_CLS[a.tipe] || TIPE_CLS.lainnya}><span className="capitalize">{a.tipe}</span></Badge></td>
                    <td className="px-5 py-3 text-slate-300">
                      <span className="text-rose-300">−{a.source_qty} {a.source_nama}</span>
                      {a.target_id && <><ArrowRight className="inline w-3.5 h-3.5 mx-1.5 text-slate-600" /><span className="text-emerald-300">+{a.target_qty} {a.target_nama}</span></>}
                    </td>
                    <td className="px-5 py-3 text-slate-400">{a.catatan || "-"}</td>
                    <td className="px-5 py-3 text-right"><button onClick={() => del(a.id)} data-testid={`delete-adj-${a.id}`} className="text-slate-500 hover:text-rose-400 transition"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
